import { useCallback } from 'react';

import { Apollo } from 'src/gql/generated';
import { useNotificationDispatch } from 'src/hooks';
import { NotificationsActionType } from 'src/notifications';
import { v4 as uuid4 } from 'uuid';

import type { AccessRequestInfo } from './useMentionAccessContext';

export interface UseRequestAccessResult {
  /** Whether a request is currently in flight */
  isRequesting: boolean;
  /**
   * Request access to one or more documents by ID.
   * Returns the created access request info (documentId + status) on success, or null on failure.
   */
  requestAccess: (documentIds: string[]) => Promise<AccessRequestInfo[] | null>;
}

/**
 * Hook for requesting access to documents.
 * Handles the mutation and toast notifications.
 *
 * Returns the created access request info from the mutation response,
 * allowing consumers to update local state without an additional network request.
 */
export function useRequestAccess(): UseRequestAccessResult {
  const dispatch = useNotificationDispatch();
  const [requestAccessToStorable, { loading }] = Apollo.useRequestAccessToStorableMutation();

  const requestAccess = useCallback(
    async (documentIds: string[]): Promise<AccessRequestInfo[] | null> => {
      if (documentIds.length === 0) return null;

      try {
        const result = await requestAccessToStorable({
          variables: {
            input: {
              storableIds: documentIds,
            },
          },
        });

        const createdRequests = result.data?.requestAccessToStorable ?? [];

        const count = documentIds.length;
        dispatch({
          payload: {
            key: uuid4(),
            message:
              count === 1
                ? 'Access request sent to document owner.'
                : `Access request sent for ${count} file${count > 1 ? 's' : ''}.`,
            variant: 'success',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });

        return createdRequests;
      } catch {
        dispatch({
          payload: {
            key: uuid4(),
            message: 'Failed to request access. Please try again.',
            variant: 'error',
          },
          type: NotificationsActionType.PUSH_NOTIFICATION,
        });
        return null;
      }
    },
    [dispatch, requestAccessToStorable]
  );

  return { isRequesting: loading, requestAccess };
}
